import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Stack,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ChatIcon from '@mui/icons-material/Chat';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../hooks/useLocalization';

export default function Prompts() {
  const { t } = useLocalization();
  const { token } = useAuth();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newPrompt, setNewPrompt] = useState('');
  const [clientSettings, setClientSettings] = useState(null);
  const [pregenerating, setPregenerating] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);

  const columns = [
    { 
      field: 'prompt_text', 
      headerName: t('prompt_text'), 
      flex: 2,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ 
            width: 32, 
            height: 32, 
            mr: 2, 
            bgcolor: 'primary.main'
          }}>
            <ChatIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Tooltip title={params.value}>
            <Typography 
              noWrap 
              sx={{ 
                fontWeight: 500,
                maxWidth: 220
              }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      )
    },
    // Only show pre-generated count column if the setting is enabled
    ...(clientSettings?.enable_pregenerated_attachments ? [{
      field: 'pregenerated_attachments_count',
      headerName: t('pregenerated_attachments'),
      flex: 2,
      minWidth: 220,
      renderCell: (params) => {
        const count = params.value || 0;
        const targetCount = clientSettings?.pregenerated_attachments_for_prompt_count || 0;
        const isComplete = count >= targetCount;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ 
              width: 28, 
              height: 28, 
              mr: 1.5, 
              bgcolor: isComplete ? 'success.main' : 'warning.main',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}>
              <AutoAwesomeIcon sx={{ fontSize: 14 }} />
            </Avatar>
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 'bold',
                  color: isComplete ? 'success.main' : 'warning.main'
                }}
              >
                {count}/{targetCount}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.7rem'
                }}
              >
                {isComplete ? t('complete') : t('incomplete')}
              </Typography>
            </Box>
          </Box>
        );
      },
    }] : []),
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Tooltip title={
          pregenerating && (jobStatus === 'pending' || jobStatus === 'running') 
            ? t('cannot_delete_prompts_while_pregenerating')
            : t('delete_prompt')
        }>
          <span>
            <IconButton
              color="error"
              onClick={() => handleDelete(params.row.prompt_id)}
              disabled={pregenerating && (jobStatus === 'pending' || jobStatus === 'running')}
              sx={{ 
                '&:hover': {
                  bgcolor: 'error.light',
                  transform: 'scale(1.1)'
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  transform: 'none'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  useEffect(() => {
    fetchPrompts(null);
    fetchClientSettings();
    checkActiveJobs();
  }, []);

  const fetchPrompts = async (event) => {
    // Prevent default behavior to avoid scrolling to top
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    try {
      // Only show loading on initial load, not on refresh
      if (!event) {
        setLoading(true);
      }
      const response = await axios.get('/prompts');
      setPrompts(response.data);
      setError('');
    } catch (err) {
      setError(t('failed_to_fetch_prompts'));
    } finally {
      if (!event) {
        setLoading(false);
      }
    }
  };

  const refreshPrompts = async (event) => {
    // Prevent default behavior to avoid scrolling to top
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    try {
      const response = await axios.get('/prompts');
      setPrompts(response.data);
      setError('');
    } catch (err) {
      setError(t('failed_to_fetch_prompts'));
    }
  };

  const fetchClientSettings = async () => {
    try {
      const response = await axios.get('/client-settings/', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientSettings(response.data);
    } catch (err) {
      console.error('Error fetching client settings:', err);
    }
  };

  const checkActiveJobs = async () => {
    try {
      const response = await axios.get('/background-jobs/active', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const activeJobs = response.data;
      const pregenerateJobs = activeJobs.filter(job => job.job_type === 'pregenerate_attachments');
      
      if (pregenerateJobs.length > 0) {
        // Use the first active pre-generation job
        const activeJob = pregenerateJobs[0];
        setCurrentJobId(activeJob.job_id);
        setJobStatus(activeJob.status);
        setJobProgress(activeJob.progress);
        setPregenerating(true);
        
        // Start polling for this job
        pollJobStatus(activeJob.job_id);
        
        console.log(`Found active pre-generation job: ${activeJob.job_id} with status: ${activeJob.status}`);
      }
    } catch (err) {
      console.error('Error checking active jobs:', err);
    }
  };

  const handlePregenerateAttachments = async () => {
    try {
      setPregenerating(true);
      setError('');
      setSuccess('');
      setJobStatus(null);
      setJobProgress(0);

      const response = await axios.post('/attachments/pregenerate', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const jobId = response.data.job_id;
      setCurrentJobId(jobId);
      setSuccess(t('pregeneration_complete'));
      
      // Start polling for job status
      pollJobStatus(jobId);
      
    } catch (err) {
      setError(err.response?.data?.detail || t('failed_to_pregenerate_attachments'));
      console.error('Error starting pre-generation:', err);
      setPregenerating(false);
    }
  };

  const pollJobStatus = async (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`/background-jobs/status/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const job = response.data;
        setJobStatus(job.status);
        setJobProgress(job.progress);
        
        if (job.status === 'completed') {
          clearInterval(pollInterval);
          setPregenerating(false);
          setCurrentJobId(null);
          setSuccess(t('pregeneration_complete'));
          
          // Refresh prompts to show updated pre-generated attachment counts
          fetchPrompts(null);
          
        } else if (job.status === 'failed') {
          clearInterval(pollInterval);
          setPregenerating(false);
          setCurrentJobId(null);
          setError(t('pregeneration_failed'));
          
        } else if (job.status === 'cancelled') {
          clearInterval(pollInterval);
          setPregenerating(false);
          setCurrentJobId(null);
          setError(t('pregeneration_failed'));
        }
        
      } catch (err) {
        console.error('Error polling job status:', err);
        clearInterval(pollInterval);
        setPregenerating(false);
        setCurrentJobId(null);
        setError(t('failed_to_pregenerate_attachments'));
      }
    }, 2000); // Poll every 2 seconds
    
    // Cleanup interval after 10 minutes (300 polls) to prevent memory leaks
    setTimeout(() => {
      clearInterval(pollInterval);
      if (pregenerating) {
        setPregenerating(false);
        setCurrentJobId(null);
        setError('Job status polling timed out. Please check manually.');
      }
    }, 600000);
  };

  const handleRefreshAfterCompletion = () => {
    setPregenerating(false);
    setCurrentJobId(null);
    setJobStatus(null);
    setJobProgress(0);
    fetchPrompts(null);
    setSuccess('');
  };

  const handleAdd = async () => {
    // Check if pregeneration is running
    if (pregenerating && (jobStatus === 'pending' || jobStatus === 'running')) {
      setError(t('cannot_delete_prompts_while_pregenerating'));
      return;
    }

    try {
      setError('');
      setSuccess('');
      const response = await axios.post('/prompts', {
        prompt_text: newPrompt
      });
      setPrompts([...prompts, response.data]);
      setOpen(false);
      setNewPrompt('');
      setSuccess(t('add_prompt_button'));
      
      // Dispatch event to notify about prompt changes
      window.dispatchEvent(new CustomEvent('promptsChanged'));
    } catch (err) {
      setError(t('failed_to_add_prompt'));
    }
  };

  const handleDelete = async (promptId) => {
    // Check if pregeneration is running
    if (pregenerating && (jobStatus === 'pending' || jobStatus === 'running')) {
      setError(t('cannot_delete_prompts_while_pregenerating'));
      return;
    }

    if (window.confirm(t('confirm_delete_prompt'))) {
      try {
        setError('');
        setSuccess('');
        await axios.delete(`/prompts/${promptId}`);
        setPrompts(prompts.filter(p => p.prompt_id !== promptId));
        setSuccess(t('delete_prompt'));
        
        // Dispatch event to notify about prompt changes
        window.dispatchEvent(new CustomEvent('promptsChanged'));
      } catch (err) {
        setError(t('failed_to_delete_prompt'));
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <ChatIcon sx={{ mr: 2, fontSize: 'inherit' }} />
          {t('prompts_management')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
          {t('manage_ai_generation_prompts')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={clientSettings?.enable_pregenerated_attachments ? 6 : 12}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2, minHeight: 160 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <ChatIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {prompts.length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('total_prompts')}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0, display: 'block', mt: 0.5 }}>
                &nbsp;
                &nbsp;
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Pre-generated Attachments Stats Card - Only show if enabled */}
        {clientSettings?.enable_pregenerated_attachments && (
          <Grid item xs={12} sm={6} md={6}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
              }
            }}>
              <CardContent sx={{ textAlign: 'center', p: 2, minHeight: 160 }}>
                <Avatar sx={{ 
                  width: 40, 
                  height: 40, 
                  mx: 'auto', 
                  mb: 1.5,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.3)'
                }}>
                  <AutoAwesomeIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {prompts.reduce((total, prompt) => total + (prompt.pregenerated_attachments_count || 0), 0)}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {t('pregenerated_attachments')}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                  {t('target_count')}: {prompts.length * (clientSettings.pregenerated_attachments_for_prompt_count || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Pre-generation Section - Only show if enabled */}
      {clientSettings?.enable_pregenerated_attachments && (
        <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 3, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ 
                width: 50, 
                height: 50, 
                mr: 2, 
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <AutoAwesomeIcon sx={{ fontSize: 25, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'white' }}>
                  {t('pregenerated_attachments')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {t('pregeneration_description').replace('{count}', clientSettings.pregenerated_attachments_for_prompt_count)}
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.3)' }} />
            
            <Box sx={{ textAlign: 'center' }}>
              {pregenerating && jobStatus ? (
                <>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 3 }}>
                    {jobStatus === 'pending' && t('job_status_pending')}
                    {jobStatus === 'running' && t('pregenerating_attachments')}
                    {jobStatus === 'completed' && t('pregeneration_complete')}
                    {jobStatus === 'failed' && t('pregeneration_failed')}
                    {jobStatus === 'cancelled' && t('pregeneration_failed')}
                  </Typography>
                  
                  {/* Job Progress Display */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
                      Status: {jobStatus.charAt(0).toUpperCase() + jobStatus.slice(1)}
                    </Typography>
                    {jobProgress > 0 && (
                      <Box sx={{ width: '100%', mb: 1 }}>
                        <Box sx={{ 
                          width: '100%', 
                          height: 8, 
                          bgcolor: 'rgba(255, 255, 255, 0.2)', 
                          borderRadius: 4,
                          overflow: 'hidden'
                        }}>
                          <Box sx={{ 
                            width: `${jobProgress}%`, 
                            height: '100%', 
                            bgcolor: 'rgba(255, 255, 255, 0.8)',
                            transition: 'width 0.3s ease'
                          }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          {jobProgress}% Complete
                        </Typography>
                      </Box>
                    )}
                    {currentJobId && (
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                        Job ID: {currentJobId}
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 3 }}>
                  {t('pregeneration_click_description').replace('{count}', clientSettings.pregenerated_attachments_for_prompt_count)}
                </Typography>
              )}
              
              <Button
                variant="contained"
                onClick={handlePregenerateAttachments}
                disabled={pregenerating && (jobStatus === 'pending' || jobStatus === 'running')}
                startIcon={pregenerating && (jobStatus === 'pending' || jobStatus === 'running') ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                sx={{ 
                  borderRadius: 2,
                  py: 2,
                  px: 4,
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: 3,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    boxShadow: 6,
                    transform: 'translateY(-2px)'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.5)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {pregenerating && jobStatus === 'pending' && t('job_status_pending')}
                {pregenerating && jobStatus === 'running' && t('pregenerating_attachments')}
                {pregenerating && jobStatus === 'completed' && t('pregeneration_complete')}
                {pregenerating && jobStatus === 'failed' && t('pregeneration_failed')}
                {pregenerating && jobStatus === 'cancelled' && t('pregeneration_failed')}
                {!pregenerating && t('pregenerate_attachments')}
              </Button>
              
              {/* Show refresh button when job is completed */}
              {pregenerating && jobStatus === 'completed' && (
                <Button
                  variant="outlined"
                  onClick={handleRefreshAfterCompletion}
                  startIcon={<RefreshIcon />}
                  sx={{ 
                    mt: 2,
                    borderRadius: 2,
                    py: 1.5,
                    px: 3,
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '2px solid rgba(255, 255, 255, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      border: '2px solid rgba(255, 255, 255, 0.7)',
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Refresh Results
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {t('available_prompts')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('manage_ai_generation_prompts')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={(e) => refreshPrompts(e)}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  fontWeight: 'bold',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.light',
                    color: 'primary.dark'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {t('refresh_prompts')}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpen(true)}
                disabled={pregenerating && (jobStatus === 'pending' || jobStatus === 'running')}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)'
                  },
                  '&:disabled': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.5)',
                    boxShadow: 'none',
                    transform: 'none'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {t('add_prompt')}
              </Button>
            </Box>
          </Box>
          
          <Paper sx={{ height: 500, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
            <DataGrid
              rows={prompts}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              getRowId={(row) => row.prompt_id}
              disableSelectionOnClick
              autoWidth
              components={{
                NoRowsOverlay: () => (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    color: 'text.secondary',
                    fontSize: '1rem',
                    fontWeight: 500
                  }}>
                    {t('no_prompts_found')}
                  </Box>
                )
              }}
              sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
                '& .MuiDataGrid-columnHeader': {
                  flex: 1,
                },
                '& .MuiDataGrid-cell': {
                  flex: 1,
                },
              }}
            />
          </Paper>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {t('add_new_prompt')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3} sx={{ mt: 2, minWidth: '500px' }}>
            <TextField
              autoFocus
              label={t('prompt_text')}
              fullWidth
              multiline
              rows={6}
              value={newPrompt}
              onChange={(e) => setNewPrompt(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              helperText={t('prompt_text_placeholder')}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleAdd} 
            variant="contained"
            disabled={!newPrompt.trim()}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            {t('add_prompt_button')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 